import type { LitNodeClient } from "@lit-protocol/lit-node-client";
import type { SessionSigsMap } from "@lit-protocol/types";
import { KEY_TYPE, LIT_ACTIONS_VERSION, MEMOS } from "../constants";
import { deriveKeyFromRoot } from "./derive-key-from-root";
import { generateWrappedKey } from "./generate";
import { AccessControlConditionsType } from "./types";
import { listWrappedKeys } from "./list";

export type KeyHierarchy = {
    version: string
    rootKeyId: string
    runnerSecretId: string
    namadaKeyId: string | undefined
}
export type PartialKeyHierarchy = Partial<KeyHierarchy>

/**
 * Generates a root key for a given PKP.
 *
 * @param litNodeClient
 * @param pkpSessionSigs
 * @returns
 */
export async function generateRootKey(
  litNodeClient: LitNodeClient,
  pkpSessionSigs: SessionSigsMap,
) {
    const memo = MEMOS.rootKey
    return generateWrappedKey(litNodeClient, pkpSessionSigs, KEY_TYPE.K256, memo, AccessControlConditionsType.VAULT)
}

/**
 * Ensures a root key is generated and stored in Lit Protocol.
 *
 * @param litNodeClient
 * @param pkpSessionSigs
 * @param keyHierarchy
 * @returns
 */
export async function ensureRootKey(
    litNodeClient: LitNodeClient,
    pkpSessionSigs: SessionSigsMap,
    keyHierarchy: PartialKeyHierarchy,
) {
    if (keyHierarchy.rootKeyId) {
        console.debug('Root key already exists')
        return keyHierarchy.rootKeyId
    }

    console.debug('Generating root key')
    const rootKey = await generateRootKey(litNodeClient, pkpSessionSigs)
    return rootKey.id
}

/**
 * Generates a Namada key for a given PKP and root key ID.
 *
 * @param litNodeClient
 * @param pkpSessionSigs
 * @param rootKeyId
 * @returns
 */
export async function generateNamadaKey(
    litNodeClient: LitNodeClient,
    pkpSessionSigs: SessionSigsMap,
    rootKeyId: string,
) {
    const memo = MEMOS.namadaWrappedKey
    return generateWrappedKey(litNodeClient, pkpSessionSigs, KEY_TYPE.NAMADA, memo, AccessControlConditionsType.VAULT, rootKeyId)
}

/**
 * Ensures a Namada key is generated and stored in Lit Protocol.
 *
 * @param litNodeClient
 * @param pkpSessionSigs
 * @param rootKeyId
 * @returns
 */
export async function ensureNamadaKey(
    litNodeClient: LitNodeClient,
    pkpSessionSigs: SessionSigsMap,
    keyHierarchy: PartialKeyHierarchy,
) {
    if (keyHierarchy.namadaKeyId) {
        console.debug('Namada key already exists')
        return keyHierarchy.namadaKeyId
    }

    // Ensure root key is generated
    if (!keyHierarchy.rootKeyId) {
        throw new Error('Root key not found')
    }

    console.debug('Generating Namada key from root key')
    const namadaKey = await generateNamadaKey(litNodeClient, pkpSessionSigs, keyHierarchy.rootKeyId)
    return namadaKey.id
}

/**
 * Derives a runner secret from a root key using domain separation.
 *
 * @param litNodeClient
 * @param pkpSessionSigs
 * @param rootKeyId
 * @returns
 */
export async function deriveRunnerSecret(
    litNodeClient: LitNodeClient,
    pkpSessionSigs: SessionSigsMap,
    rootKeyId: string,
) {
    const memo = MEMOS.derivedRunnerSecret
    const keyDomainPrefix = 'RUNNER'
    return deriveKeyFromRoot({
        litNodeClient,
        pkpSessionSigs,
        rootKeyId,
        keyDomainPrefix,
        memo,
    })
}

/**
 * Ensures a runner secret is derived and stored in Lit Protocol.
 *
 * @param litNodeClient
 * @param pkpSessionSigs
 * @param rootKeyId
 * @returns
 */
export async function ensureRunnerSecret(
    litNodeClient: LitNodeClient,
    pkpSessionSigs: SessionSigsMap,
    keyHierarchy: PartialKeyHierarchy,
) {
    if (keyHierarchy.runnerSecretId) {
        console.debug('Runner secret already exists')
        return keyHierarchy.runnerSecretId
    }

    // Ensure root key is generated
    if (!keyHierarchy.rootKeyId) {
        throw new Error('Root key not found')
    }
    
    console.debug('Deriving runner secret from root key')
    const runnerSecret = await deriveRunnerSecret(litNodeClient, pkpSessionSigs, keyHierarchy.rootKeyId)
    return runnerSecret.id
}

/**
 * Ensures a key hierarchy for a given PKP.
 *
 * @param litNodeClient
 * @param pkpSessionSigs
 * @param guestMode - Whether to generate a guest mode key hierarchy, if true, vaults will not be generated
 * @returns
 */
export async function ensureKeyHierarchy(
    litNodeClient: LitNodeClient,
    pkpSessionSigs: SessionSigsMap,
    guestMode = false
): Promise<KeyHierarchy> {
    const keyHierarchy = await getKeyHierarchy(litNodeClient, pkpSessionSigs)
    const rootKey = await ensureRootKey(litNodeClient, pkpSessionSigs, keyHierarchy)

    // Update the key hierarchy with the new root key
    keyHierarchy.rootKeyId = rootKey

    // Generate Namada key and runner secret in parallel
    const [namadaKey, runnerSecret] = await Promise.all([
        !guestMode ? ensureNamadaKey(litNodeClient, pkpSessionSigs, keyHierarchy) : undefined,
        ensureRunnerSecret(litNodeClient, pkpSessionSigs, keyHierarchy),
    ])

    return {
        version: LIT_ACTIONS_VERSION,
        rootKeyId: rootKey,
        namadaKeyId: namadaKey,
        runnerSecretId: runnerSecret,
    }
}


/**
 * Gets the key hierarchy for a given PKP.
 * 
 * @param litNodeClient 
 * @param pkpSessionSigs 
 * @returns 
 */
export async function getKeyHierarchy(
    litNodeClient: LitNodeClient,
    pkpSessionSigs: SessionSigsMap,
): Promise<PartialKeyHierarchy> {
    const allKeys = await listWrappedKeys(
        litNodeClient,
        pkpSessionSigs,
    )

    const rootKeyId = allKeys.find((key) => key.memo === MEMOS.rootKey)?.id
    const runnerSecretId = allKeys.find((key) => key.memo === MEMOS.derivedRunnerSecret)?.id
    const namadaKeyId = allKeys.find((key) => key.memo === MEMOS.namadaWrappedKey)?.id

    return {
        version: LIT_ACTIONS_VERSION,
        rootKeyId,
        runnerSecretId,
        namadaKeyId,
    }
}